import React, { useEffect, useState } from 'react';
import {
    ReferenceInput,
    SelectInput,
    TextInput,
    NumberInput,
    FormDataConsumer,
    ImageInput,
    ImageField,
    required,
} from 'react-admin';
import { useDataProvider } from 'react-admin';
import { Grid, Typography } from '@mui/material';
import { ISubCategory } from '../types/types';
import { resolveUploadUrl } from '../utils/resolveUploadUrl';
import {
    REPROCESSED_CATEGORY_FILTER,
    reprocessedSubcategoriesByCategoryFilter,
} from '../utils/materialTypeFilters';
import { useRecycleSellerContext } from './useRecycleSellerContext';

const sellerOptionText = (record: any) =>
    record.company_name || record.user_name || `${record.user_first_name || ''} ${record.user_last_name || ''}`.trim();

const RecycleSubCategorySelect = ({ categoryId }: { categoryId: number }) => {
    const [subCategories, setSubCategories] = useState<ISubCategory[]>([]);
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (categoryId) {
            dataProvider
                .getList('subcategories/bycategory', {
                    filter: reprocessedSubcategoriesByCategoryFilter(categoryId),
                    pagination: { page: 1, perPage: 200 },
                    sort: { field: 'sub_category_name', order: 'ASC' },
                })
                .then(({ data }) => setSubCategories(data))
                .catch(() => setSubCategories([]));
        } else {
            setSubCategories([]);
        }
    }, [categoryId, dataProvider]);

    return (
        <SelectInput
            source="subcategory_id"
            label="Sub Category"
            choices={subCategories}
            optionText="sub_category_name"
            optionValue="id"
            validate={[required()]}
        />
    );
};

interface StateOption {
    id: number;
    state_name: string;
}

const RecycleStateField = () => {
    const [states, setStates] = useState<StateOption[]>([]);
    const dataProvider = useDataProvider();

    useEffect(() => {
        dataProvider
            .getList('states', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'state_name', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => setStates(data))
            .catch(() => setStates([]));
    }, [dataProvider]);

    return (
        <SelectInput
            source="state_id"
            label="State"
            choices={states}
            optionText="state_name"
            optionValue="id"
            validate={[required()]}
        />
    );
};

const IMAGE_SLOTS = [
    { upload: 'image_1_upload', stored: 'image_1', label: 'Image 1' },
    { upload: 'image_2_upload', stored: 'image_2', label: 'Image 2' },
    { upload: 'image_3_upload', stored: 'image_3', label: 'Image 3' },
    { upload: 'image_4_upload', stored: 'image_4', label: 'Image 4' },
] as const;

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_SIZE_MESSAGE = 'Image size should be a maximum of 5 MB';

const validateRecycleImageUpload = (value: unknown) => {
    if (!value) return undefined;
    const files = Array.isArray(value) ? value : [value];
    for (const entry of files) {
        const rawFile = (entry as { rawFile?: File })?.rawFile;
        if (rawFile && rawFile.size > MAX_IMAGE_SIZE_BYTES) {
            return MAX_IMAGE_SIZE_MESSAGE;
        }
    }
    return undefined;
};

const CurrentImagePreview = ({ source, label }: { source: string; label: string }) => (
    <FormDataConsumer>
        {({ formData }) => {
            const path = formData?.[source];
            const uploadField = `${source}_upload`;
            if (!path || formData?.[uploadField]?.rawFile) return null;
            const src = resolveUploadUrl(path);
            return (
                <div style={{ marginTop: 8 }}>
                    <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>{label} (current)</p>
                    <img
                        src={src || path}
                        alt={label}
                        style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 4 }}
                    />
                </div>
            );
        }}
    </FormDataConsumer>
);

const RecycleImageUploads = () => (
    <Grid container spacing={2} sx={{ width: '100%' }}>
        {IMAGE_SLOTS.map(({ upload, stored, label }) => (
            <Grid item xs={12} sm={6} md={3} key={upload}>
                <ImageInput source={upload} label={label} accept="image/*" validate={[validateRecycleImageUpload]}>
                    <ImageField source="src" title="title" />
                </ImageInput>
                <CurrentImagePreview source={stored} label={label} />
            </Grid>
        ))}
    </Grid>
);

const SellerField = () => {
    const { isSeller, sellerId, sellerLabel } = useRecycleSellerContext();

    if (isSeller && sellerId) {
        return (
            <FormDataConsumer>
                {({ formData }) => (
                    <TextInput
                        source="seller_name"
                        label="Seller"
                        defaultValue={formData?.seller_name || sellerLabel}
                        disabled
                    />
                )}
            </FormDataConsumer>
        );
    }

    return (
        <ReferenceInput source="seller_id" reference="users" label="Seller" filter={{ role_id: 3 }}>
            <SelectInput optionText={sellerOptionText} validate={[required()]} />
        </ReferenceInput>
    );
};

export const RecycleProductFormFields = ({ showAdminStatus = false }: { showAdminStatus?: boolean }) => {
    const { isSeller } = useRecycleSellerContext();

    return (
    <>
        <FormDataConsumer>
            {({ formData }) =>
                formData?.rejection_reason ? (
                    <Typography color="error" sx={{ mb: 2 }}>
                        Rejection reason: {formData.rejection_reason}
                    </Typography>
                ) : null
            }
        </FormDataConsumer>

        {showAdminStatus && !isSeller && (
            <SelectInput
                source="status"
                label="Status"
                choices={[
                    { id: 'draft', name: 'Draft' },
                    { id: 'submitted', name: 'Submitted' },
                    { id: 'approved', name: 'Approved' },
                    { id: 'rejected', name: 'Rejected' },
                    { id: 'archived', name: 'Archived' },
                ]}
                validate={[required()]}
            />
        )}

        {isSeller && (
            <FormDataConsumer>
                {({ formData }) =>
                    formData?.status ? (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Current status: <strong>{formData.status}</strong>
                        </Typography>
                    ) : null
                }
            </FormDataConsumer>
        )}

        <ReferenceInput
            source="category_id"
            reference="categories"
            label="Category"
            filter={REPROCESSED_CATEGORY_FILTER}
        >
            <SelectInput optionText="category_name" validate={[required()]} />
        </ReferenceInput>

        <FormDataConsumer>
            {({ formData }) =>
                formData.category_id ? <RecycleSubCategorySelect categoryId={formData.category_id} /> : null
            }
        </FormDataConsumer>

        <TextInput source="title" label="Title" fullWidth validate={[required()]} />

        <SelectInput
            source="market_segment"
            label="Market segment"
            choices={[
                { id: 'recycled', name: 'Recycled / reprocessed' },
                { id: 'virgin_surplus', name: 'Virgin surplus' },
                { id: 'trader_stock', name: 'Trader stock' },
            ]}
            defaultValue="recycled"
            validate={[required()]}
        />

        <RecycleStateField />

        <TextInput
            source="description"
            label="Description"
            multiline
            fullWidth
            rows={8}
            validate={[required()]}
        />

        <NumberInput source="price" label="Price (INR)" min={0} step={0.01} validate={[required()]} />

        <SellerField />

        <RecycleImageUploads />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
            Max 5 MB per image
        </Typography>
    </>
    );
};

export function buildRecycleProductFormData(
    values: any,
    sellerIdOverride?: number | null,
    options: { statusAction?: string; status?: string } = {}
): FormData {
    const formData = new FormData();
    formData.append('category_id', String(values.category_id));
    formData.append('subcategory_id', String(values.subcategory_id));
    formData.append('title', values.title || '');
    formData.append('state_id', values.state_id != null ? String(values.state_id) : '');
    formData.append('description', values.description || '');
    formData.append('price', values.price != null ? String(values.price) : '');
    const sellerId = sellerIdOverride ?? values.seller_id;
    formData.append('seller_id', String(sellerId));
    if (options.statusAction) {
        formData.append('status_action', options.statusAction);
    }
    if (options.status) {
        formData.append('status', options.status);
    }

    ['image_1', 'image_2', 'image_3', 'image_4'].forEach((field) => {
        const upload = values[`${field}_upload`];
        if (upload?.rawFile) {
            formData.append(field, upload.rawFile);
        }
    });

    return formData;
}

export async function submitRecycleProduct(
    method: 'POST' | 'PUT',
    path: string,
    values: any,
    sellerIdOverride?: number | null,
    options: { statusAction?: string; status?: string } = {}
): Promise<Response> {
    const formData = buildRecycleProductFormData(values, sellerIdOverride, options);
    const token = localStorage.getItem('user_authToken');
    return fetch(`${process.env.REACT_APP_API_URL}${path}`, {
        method,
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
}

export async function postRecycleAction(path: string, body?: Record<string, unknown>): Promise<Response> {
    const token = localStorage.getItem('user_authToken');
    return fetch(`${process.env.REACT_APP_API_URL}${path}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
}
